// src/contexts/AuthContext.js
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  query,
  setDoc,
  where,
} from "firebase/firestore";
import React, { useContext, useEffect, useState } from "react";
import { auth, firestore } from "../../firebase";
import { isChatEnabledPriceId } from "../../constants";

// Stripe extension subscription statuses that count as "paid/active".
const ACTIVE_SUBSCRIPTION_STATUSES = ["active", "trialing"];

// Resolve the Stripe price id from a subscription doc written by the
// Firebase Stripe Payments extension. `price` and `items[].price` are
// Firestore DocumentReferences whose `.id` is the Stripe price id.
function resolveActivePriceId(subData) {
  if (!subData) return null;
  if (subData.price && subData.price.id) return subData.price.id;
  const firstItem = Array.isArray(subData.items) ? subData.items[0] : null;
  if (firstItem && firstItem.price && firstItem.price.id) return firstItem.price.id;
  return null;
}

const AuthContext = React.createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [accountData, setAccountData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [activePriceId, setActivePriceId] = useState(null);

  // Auth + account: one-time per auth change; gates the initial loading screen.
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      setCurrentUser(user);
      if (!user) {
        setAccountData(null);
        setLoading(false);
        return;
      }

      const userDocRef = doc(firestore, "accounts", user.uid);
      try {
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          setAccountData({ ...userDoc.data(), id: userDoc.id });
        } else {
          await setDoc(userDocRef, { admins: [user.email], teamMembers: [] });
          setAccountData({ admins: [user.email], teamMembers: [], id: userDocRef.id });
        }
      } catch (accountError) {
        console.error(`ACCOUNT_LOAD_FAILED uid=${user.uid} reason=${accountError.message}`);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  // Subscription/entitlement: live listener so plan changes (and the Stripe
  // extension's async webhook writes) reflect without a re-login. Resolves
  // across ALL active subscriptions and PREFERS a chat-enabled tier, so a
  // lingering legacy/no-chat subscription can't mask a chat upgrade.
  useEffect(() => {
    if (!currentUser) {
      setIsSubscribed(false);
      setSubscription(null);
      setActivePriceId(null);
      return undefined;
    }

    const customerRef = doc(collection(firestore, "customers"), currentUser.uid);
    let legacyActive = false;
    getDoc(customerRef)
      .then((snap) => {
        legacyActive = snap.data()?.activeSubscription || false;
      })
      .catch(() => {});

    const activeSubsQuery = query(
      collection(customerRef, "subscriptions"),
      where("status", "in", ACTIVE_SUBSCRIPTION_STATUSES)
    );

    const unsubscribe = onSnapshot(
      activeSubsQuery,
      (snap) => {
        const subs = snap.docs.map((d) => ({ ...d.data(), id: d.id }));
        const hasActiveSub = subs.length > 0;
        // Prefer a chat-enabled subscription for the primary/display plan.
        const chatSub = subs.find((s) => isChatEnabledPriceId(resolveActivePriceId(s)));
        const primary = chatSub || subs[0] || null;
        const priceId = resolveActivePriceId(primary);

        setIsSubscribed(hasActiveSub || legacyActive);
        setSubscription(primary);
        setActivePriceId(priceId);

        console.info(
          `SUBSCRIPTION_RESOLVED uid=${currentUser.uid} active=${
            hasActiveSub || legacyActive
          } subs=${subs.length} priceId=${priceId || "none"} chatEntitled=${isChatEnabledPriceId(priceId)}`
        );
      },
      (subError) => {
        setIsSubscribed(false);
        setSubscription(null);
        setActivePriceId(null);
        console.error(`SUBSCRIPTION_LISTEN_FAILED uid=${currentUser.uid} reason=${subError.message}`);
      }
    );

    return () => unsubscribe();
  }, [currentUser]);

  // Chat entitlement is derived purely from the active Stripe price id. A legacy
  // `activeSubscription` flag (no price id) does NOT unlock chat — chat requires
  // one of the explicit chat-tier prices. Server functions re-check this before
  // any chat write; this flag is for UX gating only.
  const chatEntitled = isChatEnabledPriceId(activePriceId);

  const value = {
    currentUser,
    accountData,
    isSubscribed,
    subscription,
    activePriceId,
    chatEntitled,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
